import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';

interface MediaItem {
  id: string;
  name: string;
  url: string;
  type: string;
  size_bytes: number;
  created_at: string;
}

export default function AdminMediaPage() {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const [urlName, setUrlName] = useState('');
  const [addingUrl, setAddingUrl] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { loadMedia(); }, []);

  const loadMedia = async () => {
    const { data } = await supabase
      .from('media_library')
      .select('*')
      .order('created_at', { ascending: false });
    setItems(data || []);
    setLoading(false);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);

    for (const file of Array.from(files)) {
      const ext = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      const { data: uploadData, error } = await supabase.storage
        .from('media')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (!error && uploadData) {
        const { data: urlData } = supabase.storage.from('media').getPublicUrl(uploadData.path);
        await supabase.from('media_library').insert({
          name: file.name,
          url: urlData.publicUrl,
          type: file.type.startsWith('video') ? 'video' : 'image',
          size_bytes: file.size,
        });
      }
    }

    await loadMedia();
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleAddUrl = async () => {
    if (!urlInput.trim()) return;
    setAddingUrl(true);
    await supabase.from('media_library').insert({
      name: urlName || urlInput.split('/').pop() || 'imagem',
      url: urlInput.trim(),
      type: 'image',
      size_bytes: 0,
    });
    setUrlInput('');
    setUrlName('');
    await loadMedia();
    setAddingUrl(false);
  };

  const handleDelete = async (id: string) => {
    await supabase.from('media_library').delete().eq('id', id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Biblioteca de Mídia</h1>
          <p className="text-gray-400 text-sm mt-0.5">Gerencie imagens e vídeos do site</p>
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="bg-[#C9A84C] hover:bg-[#D4AF37] text-black text-sm font-semibold px-5 py-2.5 rounded-lg whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {uploading ? <><i className="ri-loader-4-line animate-spin"></i> Enviando...</> : <><i className="ri-upload-line"></i> Upload</>}
        </button>
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFileUpload} />
      </div>

      {/* Add by URL */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Adicionar por URL</h2>
        <div className="flex gap-3">
          <input
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C9A84C] transition-colors"
            placeholder="https://exemplo.com/imagem.jpg"
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
          />
          <input
            className="w-40 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C9A84C] transition-colors"
            placeholder="Nome (opcional)"
            value={urlName}
            onChange={e => setUrlName(e.target.value)}
          />
          <button
            onClick={handleAddUrl}
            disabled={addingUrl || !urlInput.trim()}
            className="bg-gray-800 hover:bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg whitespace-nowrap cursor-pointer transition-colors disabled:opacity-40 flex items-center gap-2"
          >
            {addingUrl ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
            Adicionar
          </button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <i className="ri-loader-4-line text-[#C9A84C] text-2xl animate-spin"></i>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-full">
            <i className="ri-image-2-line text-gray-400 text-xl"></i>
          </div>
          <p className="text-gray-500 text-sm">Nenhuma mídia ainda</p>
          <p className="text-gray-400 text-xs mt-1">Faça upload de imagens ou adicione por URL</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((item) => (
            <div key={item.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden group">
              <div className="relative h-32 bg-gray-50">
                {item.type === 'image' ? (
                  <img src={item.url} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-video-line text-gray-300 text-3xl"></i>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleCopy(item.url)}
                    className="w-8 h-8 flex items-center justify-center bg-white rounded-lg cursor-pointer hover:bg-[#C9A84C] transition-colors"
                    title="Copiar URL"
                  >
                    <i className={`${copied === item.url ? 'ri-check-line text-emerald-600' : 'ri-clipboard-line text-gray-700'} text-sm`}></i>
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="w-8 h-8 flex items-center justify-center bg-white rounded-lg cursor-pointer hover:bg-red-500 hover:text-white transition-colors"
                    title="Excluir"
                  >
                    <i className="ri-delete-bin-line text-gray-700 text-sm"></i>
                  </button>
                </div>
              </div>
              <div className="p-2.5">
                <p className="text-xs text-gray-700 font-medium truncate">{item.name}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{formatSize(item.size_bytes)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
